import * as React from 'react';
import type { EmailAddressResource } from '@clerk/types';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Box, Button, Fade, Stack, TextField, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { ClerkAPIError } from '@clerk/types';
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors';
import OtpCode from './otp-code';
import { useNotifications } from '@/hooks/use-notifications';
import { useState } from 'react';
import { buildApiUrl } from '@/utils/build-api-url.ts';

async function revokeClerkContributorInvitation(contributorId: string, token: string | null) {
  if (!token || !import.meta.env.VITE_API_URL) return;
  try {
    const res = await fetch(
      buildApiUrl(`invitations/contributors/${encodeURIComponent(contributorId)}/revoke-clerk-invitation`),
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!res.ok && res.status !== 404) {
      console.warn('revoke-clerk-invitation failed', res.status);
    }
  } catch (e) {
    console.warn('revoke-clerk-invitation error', e);
  }
}

export default function NewEmailVerification({
  email,
  contributorId,
  onSuccess,
}: {
  email: string;
  contributorId?: string;
  onSuccess: () => void;
}) {
  return (
    <Fade in={true} timeout={800}>
      <Box>
        <NewEmailVerificationInner email={email} contributorId={contributorId} onSuccess={onSuccess} />
      </Box>
    </Fade>
  );
}

function DisplayErrors({ errors }: { errors: ClerkAPIError[] }) {
  return (
    <Stack spacing={1}>
      {errors.map((el, index) => (
        <Typography
          key={index}
          color="error"
          sx={{
            'fontSize': '0.875rem',
            '&::before': {
              content: '"• "',
              marginRight: '4px',
            },
          }}
        >
          {el.longMessage}
        </Typography>
      ))}
    </Stack>
  );
}

function NewEmailVerificationInner({
  email,
  contributorId,
  onSuccess,
}: {
  email: string;
  contributorId?: string;
  onSuccess: () => void;
}) {
  const { isLoaded, user } = useUser();
  const { getToken } = useAuth();
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [OTPVerifyInProgress, setOTPVerifyInProgress] = useState(false);
  const [emailObj, setEmailObj] = useState<EmailAddressResource | undefined>();
  const [errors, setErrors] = useState<ClerkAPIError[]>();
  const notification = useNotifications();
  const t = useTranslations();

  if (!isLoaded || !user?.id) return;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOTPVerifyInProgress(true);
    setErrors(undefined);

    try {
      const res = await user.createEmailAddress({ email });
      await user.reload();

      const emailAddress = user.emailAddresses.find((a) => a.id === res.id);
      setEmailObj(emailAddress);

      emailAddress?.prepareVerification({ strategy: 'email_code' });

      setIsVerifying(true);
    } catch (err) {
      if (isClerkAPIResponseError(err)) setErrors(err.errors);
    }
    setOTPVerifyInProgress(false);
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors(undefined);
    try {
      const emailVerifyAttempt = await emailObj?.attemptVerification({ code });

      if (emailVerifyAttempt?.verification.status === 'verified') {
        if (contributorId) {
          const token = await getToken();
          await revokeClerkContributorInvitation(contributorId, token);
        }
        notification.notifySuccess(t('user.notification.emailVerified'));
        onSuccess();
        return;
      }
      console.error(JSON.stringify(emailVerifyAttempt, null, 2));
    } catch (err) {
      if (isClerkAPIResponseError(err)) setErrors(err.errors);
    }
  };

  const otpChanged = (code: string) => {
    setCode(code);
    setErrors(undefined);
  };

  if (isVerifying) {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('invitationFlow.newEmailDetected.otpExplanation', { email })}
        </Typography>
        <form onSubmit={(e) => verifyCode(e)}>
          <Stack spacing={3}>
            <Box sx={{ py: 4 }}>
              <OtpCode autoFocus onChange={(e) => otpChanged(e)} hasError={!!errors?.length} />
            </Box>
            <DisplayErrors errors={errors ?? []} />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{ borderRadius: 2 }}
                disabled={code.length !== 6 || OTPVerifyInProgress}
              >
                {t('common.verify')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('invitationFlow.newEmailDetected.explanation', { email })}
      </Typography>
      <Box component="form" onSubmit={(e) => handleSubmit(e)}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            label={t('common.email')}
            slotProps={{ htmlInput: { readOnly: true } }}
            defaultValue={email}
            sx={{ mt: 2 }}
          />
          <DisplayErrors errors={errors ?? []} />
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            alignItems="end"
            sx={{ '&.MuiStack-root': { mt: 0 } }}
          >
            <Button type="submit" variant="contained" color="primary" size="large" sx={{ borderRadius: 2 }}>
              {t('invitationFlow.newEmailDetected.sendEmail')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </>
  );
}
